import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  Box,
} from "@mui/material";
import {
  useHeyGenStore,
} from "../../../stores/heygenStore";
import { useLiveKitConnection } from "../../../hooks/useLiveKitConnection";
import { useAppStore } from "../../../stores/appStore";
import { useUserStore } from "../../../stores/userStore";
import { CountdownStart } from "../../countdown-start/countdown-start";
import {
  containerStyles,
  countdownOverlayStyles,
  sessionLayoutRootStyles,
  videoColumnStyles,
  videoStagePanelStyles,
  videoStageStyles,
} from "./styles";
import { useVoiceChat } from "../../../hooks/useVoiceChat";
import { useRemoteAudioAnalysis } from "../../../hooks/useRemoteAudioAnalysis";
import { useAudioRecording } from "../../../hooks/useAudioRecording";
import { useSessionLifecycle } from "../../../hooks/useSessionLifecycle";
import { NoiseAnalyzer } from "../../../utils/noiseAnalysis";
import { forceStopAllMediaStreams } from "../../../utils/mediaStreamCleanup";
import { AvatarVideoDisplay } from "./avatar-video-display";
import { AvatarHeaderInfo } from "./avatar-header-info";
import { AvatarControls } from "./avatar-controls";
import {
  AvatarSessionContextPanel,
  type AvatarSessionStatus,
} from "./avatar-session-context-panel";
import { usePushToTalk } from "../../../hooks/usePushToTalk";
import { extractLiveDoctorName } from "../../../utils/transcription";
import { END_OF_UTTERANCE_SILENCE_MS } from "../../../utils/trailingSilence";
import { LiveSpeechSessionErrorModal } from "../../live-speech-session-error-modal/live-speech-session-error-modal";
import type { LiveSpeechSessionError } from "../../../types/liveSpeechSessionError";
import {
  OFFLINE_SESSION_ERROR,
  isBrowserOffline,
} from "../../../types/liveSpeechSessionError";

interface HeyGenAvatarProps {
  avatarId?: string;
  voiceId?: string;
  onSessionEnd?: () => void;
  onError?: (error: string) => void;
  className?: string;
  auth?: {
    [key: string]: any;
  };
  user_id?: string;
  onEndSessionReady?: (
    endSession: (options?: { close?: boolean }) => Promise<void>,
  ) => void;
  onFallbackAudioOnly?: () => void | Promise<void>;
}


export const HeyGenAvatar: React.FC<HeyGenAvatarProps> = ({
  avatarId,
  voiceId,
  onSessionEnd,
  onError,
  className,
  auth,
  user_id,
  onEndSessionReady,
  onFallbackAudioOnly,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const remoteTracksRef = useRef<Array<{ track: any; kind: string }>>([]);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [isWarningError, setIsWarningError] = useState(false);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  const [isMicDisabledByWarning, setIsMicDisabledByWarning] = useState(false);
  const secondsRemainingRef = useRef<number | null>(null);
  const noiseCheckDoneRef = useRef(false);
  const noiseWarningShownRef = useRef(false);
  const isProcessingPausedRef = useRef(false);
  const endSessionRef = useRef<(() => Promise<void>) | null>(null);
  const closeOnVideoLostRef = useRef<(() => void) | null>(null);
  const stopAudioRecordingRef = useRef<
    ((shouldStopTracks?: boolean) => Promise<void>) | null
  >(null);
  // ---------- Noise/SNR analysis ----------
  const noiseAnalyzerRef = useRef<NoiseAnalyzer>(new NoiseAnalyzer());

  const {
    realtimePersona,
    realtimeMaturityLevel,
    lessonDialogue,
    sellingDialogue,
    lessonSecondsAllowed,
    lessonSecondsRemaining,
    setLessonSecondsAllowed,
    setLessonSecondsRemaining,
    liveSpeechMode,
    realtimeDetailsWindowText,
    domain,
    focus,
  } = useAppStore();
  const { getFocusDisplayName } = useUserStore();
  const focusDisplayName = getFocusDisplayName(focus, domain);

  const isRolePlay = Boolean(sellingDialogue) && !lessonDialogue;
  const isCoachingMode = liveSpeechMode === "coach_me";
  const useSessionContextLayout = isRolePlay || isCoachingMode;
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);

  const [sessionError, setSessionError] =
    useState<LiveSpeechSessionError | null>(null);


  const {
    isConnected,
    isConnecting,
    // error,
    isRecording,
    audioStream,
    liveTranscript,
    windowTitle,
    setLiveKitData,
    setIsConnected,
    setIsConnecting,
    setError,
    setIsRecording,
    setAudioStream,
    setSessionActive,
    setWindowTitle,
    reset,
  } = useHeyGenStore();

  const doctorName = useMemo(
    () => extractLiveDoctorName(windowTitle, realtimePersona?.text),
    [windowTitle, realtimePersona?.text],
  );

  // Create refs for functions that need to be passed between hooks
  const setupRemoteAudioAnalysisRef = useRef<
    | ((
        audioTrack: any,
        options?: {
          onAiSpeechDetected?: () => void;
          onAiSpeakingStateChange?: (speaking: boolean) => void;
        },
      ) => void)
    | null
  >(null);
  const startAudioRecordingRef = useRef<(() => Promise<void>) | null>(null);
  const stopAudioRecordingRefForAutoMute = useRef<
    | ((
        shouldStopTracks?: boolean,
        options?: { finalizeTurn?: boolean; reason?: string }
      ) => Promise<void>)
    | null
  >(null);

  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    initializeSocket,
    getAudioStream,
    prepareDisconnect,
    clearSessionError,
    retrySession,
    markUserSpoken,
    roomRef,
    socketRef,
  } = useLiveKitConnection({
    auth,
    videoRef,
    audioStreamRef,
    remoteTracksRef,
    setIsConnecting,
    setIsConnected,
    setError,
    setLiveKitData,
    setAudioStream,
    setSessionActive,
    setLessonSecondsAllowed,
    setLessonSecondsRemaining,
    setWindowTitle,
    onError,
    onSessionError: setSessionError,
    onRemoteVideoLost: () => {
      closeOnVideoLostRef.current?.();
    },
    setupRemoteAudioAnalysis: (
      audioTrack: any,
      options?: {
        onAiSpeechDetected?: () => void;
        onAiSpeakingStateChange?: (speaking: boolean) => void;
      },
    ) => {
      setupRemoteAudioAnalysisRef.current?.(audioTrack, {
        ...options,
        onAiSpeakingStateChange: (speaking: boolean) => {
          setIsAiSpeaking(speaking);
          options?.onAiSpeakingStateChange?.(speaking);
        },
      });
    },
  });

  const {
    startAudioRecording,
    stopAudioRecording,
    cancelTrailingSilence,
    chunkQueue,
    isCurrentlySendingRef,
    silenceTimeoutRef,
    wavRecorderRef,
  } = useAudioRecording({
    user_id,
    avatarId,
    voiceId,
    socketRef,
    audioStreamRef,
    isMuted,
    isRecording,
    setIsRecording,
    setAudioStream,
    isProcessingPausedRef,
    noiseAnalyzerRef,
    noiseWarningShownRef,
    noiseCheckDoneRef,
    setIsWarningError,
    setIsMicDisabledByWarning,
    setWarningMessage,
  });

  useEffect(() => {
    startAudioRecordingRef.current = startAudioRecording;
  }, [startAudioRecording]);

  useEffect(() => {
    stopAudioRecordingRef.current = stopAudioRecording;
    stopAudioRecordingRefForAutoMute.current = stopAudioRecording;
  }, [stopAudioRecording]);

  const { setupRemoteAudioAnalysis, userMutedManuallyRef, cleanup: cleanupRemoteAudio } = useRemoteAudioAnalysis({
    isMuted,
    setIsMuted,
    isRecording,
    isConnected,
    audioStreamRef,
    startAudioRecordingRef,
    stopAudioRecordingRefForAutoMute,
    isPushToTalk: isRolePlay,
  });

  useEffect(() => {
    setupRemoteAudioAnalysisRef.current = setupRemoteAudioAnalysis;
  }, [setupRemoteAudioAnalysis]);

  const { initializeSession, endSession } = useSessionLifecycle({
    isConnecting,
    isInitializing,
    setIsInitializing,
    isConnected,
    audioStreamRef,
    initializeSocket,
    getAudioStream,
    setError,
    setSessionActive,
    onError,
    stopAudioRecording,
    roomRef,
    socketRef,
    setAudioStream,
    silenceTimeoutRef,
    cleanupRemoteAudio,
    chunkQueue,
    isCurrentlySendingRef,
    noiseAnalyzerRef,
    noiseCheckDoneRef,
    noiseWarningShownRef,
    isProcessingPausedRef,
    userMutedManuallyRef,
    remoteTracksRef,
    setWarningMessage,
    setIsWarningError,
    setIsMicDisabledByWarning,
    reset,
    onSessionEnd,
    prepareDisconnect,
    wavRecorderRef,
  });

  useEffect(() => {
    if (lessonDialogue && liveSpeechMode !== "coach_me") {
      setShowCountdown(true);
    } else {
      setShowCountdown(false);
      if (!lessonDialogue || liveSpeechMode === "coach_me") {
        initializeSession();
      }
    }

    const handleBeforeUnload = () => {
      stopAudioRecording(true);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopAudioRecording(false);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }

      cleanupRemoteAudio();

      chunkQueue.current = [];
      isCurrentlySendingRef.current = false;
      // eslint-disable-next-line react-hooks/exhaustive-deps
      noiseAnalyzerRef.current.reset();

      stopAudioRecording(true);

      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      const currentRoom = roomRef.current;

      prepareDisconnect();

      if (currentRoom) {
        currentRoom.disconnect().catch(() => {});
        roomRef.current = null;
      }

      forceStopAllMediaStreams(audioStreamRef, wavRecorderRef);

      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }

      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avatarId, voiceId, lessonDialogue]);


  useEffect(() => {
    endSessionRef.current = endSession;
    closeOnVideoLostRef.current = () => {
      void endSession();
    };
  }, [endSession]);

  useEffect(() => {
    if (onEndSessionReady) {
      onEndSessionReady(endSession);
    }
  }, [endSession, onEndSessionReady]);

  const {
    muteInputAudio: originalMuteInputAudio,
    unmuteInputAudio: originalUnmuteInputAudio,
    isVoiceChatLoading,
  } = useVoiceChat({
    isMuted,
    setIsMuted,
    isConnected,
    isRecording,
    audioStreamRef,
    startAudioRecording,
    stopAudioRecording,
    cancelTrailingSilence,
    initializeSession,
    isMicDisabledByWarning,
    chunkQueue,
    isCurrentlySendingRef,
    silenceTimeoutRef,
  });

  // Wrapper function for tracking manual microphone mute
  const muteInputAudio = useCallback(() => {
    userMutedManuallyRef.current = true;
    originalMuteInputAudio(
      isRolePlay ? { trailingSilenceMs: END_OF_UTTERANCE_SILENCE_MS } : undefined,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalMuteInputAudio, isRolePlay]);

  const unmuteInputAudio = useCallback(() => {
    userMutedManuallyRef.current = false;
    if (isRolePlay) {
      markUserSpoken();
    }
    originalUnmuteInputAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalUnmuteInputAudio, isRolePlay, markUserSpoken]);

  const {
    buttonRef: pushButtonRef,
    isPressed: isPushPressed,
    pushToTalkHandlers,
  } = usePushToTalk({
    enabled: isRolePlay,
    onPressStart: unmuteInputAudio,
    onPressEnd: muteInputAudio,
  });

  useEffect(() => {
    if (isRolePlay && isConnected && !isMuted && !isPushPressed) {
      muteInputAudio();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRolePlay, isConnected]);

  useEffect(() => {
    if (
      isConnected &&
      !isMuted &&
      audioStreamRef.current &&
      !isRecording &&
      !showCountdown &&
      !isProcessingPausedRef.current
    ) {
      if (!isRolePlay) {
        markUserSpoken();
      }
      startAudioRecording();
    }
  }, [
    isConnected,
    isMuted,
    isRecording,
    showCountdown,
    startAudioRecording,
    isRolePlay,
    markUserSpoken,
  ]);

  const handleCountdownComplete = useCallback(() => {
    setShowCountdown(false);
    initializeSession();
  }, [initializeSession]);

  const handleStopSession = useCallback(async () => {
    await endSession();
  }, [endSession]);

  const handleSessionExit = useCallback(async () => {
    setSessionError(null);
    await endSession();
  }, [endSession]);

  const handleSessionRetry = useCallback(async () => {
    if (isBrowserOffline()) {
      clearSessionError();
      setSessionError({
        ...OFFLINE_SESSION_ERROR,
        canFallbackAudioOnly: true,
      });
      return;
    }

    setSessionError(null);
    clearSessionError();
    setIsInitializing(false);
    await retrySession();
  }, [clearSessionError, retrySession]);

  const handleFallbackAudioOnly = useCallback(async () => {
    setSessionError(null);
    clearSessionError();

    // Tear down LiveKit/socket *before* the parent remounts audio.
    try {
      await endSession({ close: false });
    } catch (error) {
      console.error("Failed to end video session before audio fallback:", error);
    }

    await onFallbackAudioOnly?.();
  }, [clearSessionError, endSession, onFallbackAudioOnly]);

  useEffect(() => {
    if (showCountdown) {
      return;
    }

    if (
      !lessonSecondsAllowed ||
      !lessonSecondsRemaining ||
      lessonSecondsRemaining <= 0
    ) {
      return;
    }

    secondsRemainingRef.current = lessonSecondsRemaining;

    const interval = setInterval(() => {
      if (
        secondsRemainingRef.current === null ||
        secondsRemainingRef.current <= 1
      ) {
        handleStopSession();
        setLessonSecondsRemaining(0);
      } else {
        secondsRemainingRef.current -= 1;
        setLessonSecondsRemaining(secondsRemainingRef.current);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [
    lessonSecondsAllowed,
    lessonSecondsRemaining,
    showCountdown,
    handleStopSession,
    setLessonSecondsRemaining,
  ]);

  const sessionObjective = useMemo(() => {
    let fromDetails = realtimeDetailsWindowText?.trim() || "";
    if (fromDetails) {
      fromDetails = fromDetails.replace(/\*\*([^*]+)\*\*/g, "$1");
      fromDetails = fromDetails.replace(/\*/g, "");

      const title = windowTitle?.trim();
      if (title && fromDetails.startsWith(title)) {
        fromDetails = fromDetails.slice(title.length).trim();
      }
      // Also drop a leading "Sales Meeting with … Persona …" block if present.
      fromDetails = fromDetails
        .replace(
          /^Sales Meeting with[\s\S]*?(?=Practice Domain:|Stage:|Objection:|$)/i,
          "",
        )
        .trim();

      fromDetails = fromDetails
        .replace(
          /\s+(Practice Domain:|Stage:|Adoption Sub-Step:|Sub-Step:|Objection:)/gi,
          "\n$1",
        )
        .trim();

      if (fromDetails) return fromDetails;
    }
    if (isCoachingMode) {
      return "Practice with your coach — listen carefully and respond clearly.";
    }
    return "Talk to the HCP, listen to objections, and respond clearly.";
  }, [realtimeDetailsWindowText, isCoachingMode, windowTitle]);

  const sessionStatus: AvatarSessionStatus = useMemo(() => {
    if (!isConnected || isConnecting || isInitializing) return "Connecting";
    if (isRolePlay ? isPushPressed : isRecording && !isMuted) return "Speaking";
    if (isAiSpeaking) return "AI speaking";
    return "Listening";
  }, [
    isConnected,
    isConnecting,
    isInitializing,
    isRolePlay,
    isPushPressed,
    isRecording,
    isMuted,
    isAiSpeaking,
  ]);

  const handleWarningContinue = useCallback(async () => {
    if (isRolePlay) {
      setWarningMessage(null);
      setIsWarningError(false);
      noiseAnalyzerRef.current.clearWarning();
      return;
    }

    setWarningMessage(null);
    setIsWarningError(false);
    setIsMicDisabledByWarning(false);

    isProcessingPausedRef.current = false;
    noiseWarningShownRef.current = false;
    noiseCheckDoneRef.current = false;
    noiseAnalyzerRef.current.clearWarning();
    noiseAnalyzerRef.current.reset();

    remoteTracksRef.current.forEach(({ track }) => {
      if (track && typeof track.setEnabled === "function") {
        track.setEnabled(true);
      }
    });

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => {
        track.enabled = true;
      });
    }

    if (!isRecording) {
      await startAudioRecording();
    }
  }, [isRolePlay, isRecording, startAudioRecording]);

  const handleWarningClose = useCallback(() => {
    if (endSessionRef.current) {
      endSessionRef.current();
    }
  }, []);

  const showStartCountdown =
    showCountdown && lessonDialogue && liveSpeechMode !== "coach_me";

  const headerAndVideo = (
    <>
      <AvatarHeaderInfo
        showCountdown={showCountdown}
        realtimePersona={realtimePersona}
        realtimeMaturityLevel={realtimeMaturityLevel}
        lessonSecondsAllowed={lessonSecondsAllowed}
        lessonSecondsRemaining={lessonSecondsRemaining}
        isConnected={isConnected}
        liveSpeechMode={liveSpeechMode}
        onTimeUp={handleStopSession}
        hidePersonaOverlay={useSessionContextLayout}
      />

      <AvatarVideoDisplay
        videoRef={videoRef}
        isConnecting={isConnecting}
        isInitializing={isInitializing}
        isConnected={isConnected}
        warningMessage={warningMessage}
        isWarningError={isWarningError}
        isRolePlay={isRolePlay}
        preferContainedFraming={useSessionContextLayout}
        onWarningContinue={handleWarningContinue}
        onWarningClose={handleWarningClose}
      />
    </>
  );

  const sharedControlsProps = {
    isConnected,
    showCountdown,
    isRecording,
    isMuted,
    isMicDisabledByWarning,
    isVoiceChatLoading,
    muteInputAudio,
    unmuteInputAudio,
    isPushToTalk: isRolePlay,
    isPushPressed,
    mediaStream: audioStream,
    pushButtonRef,
    pushToTalkHandlers,
    onEndCall: isRolePlay ? handleStopSession : undefined,
  };

  const controls = (
    <AvatarControls
      {...sharedControlsProps}
      docked={useSessionContextLayout}
      hideWave={useSessionContextLayout}
    />
  );

  const inVideoWave = useSessionContextLayout ? (
    <AvatarControls {...sharedControlsProps} docked waveOverlayOnly />
  ) : null;

  const sessionErrorModal = (
    <LiveSpeechSessionErrorModal
      error={sessionError}
      onRetry={
        sessionError?.retryable ? () => void handleSessionRetry() : undefined
      }
      onFallbackAudioOnly={
        sessionError?.canFallbackAudioOnly && onFallbackAudioOnly
          ? () => void handleFallbackAudioOnly()
          : undefined
      }
      onExit={() => void handleSessionExit()}
    />
  );

  if (useSessionContextLayout && !showStartCountdown) {
    return (
      <Box className={className} sx={sessionLayoutRootStyles}>
        <Box sx={videoColumnStyles}>
          <Box sx={videoStagePanelStyles}>
            <Box sx={videoStageStyles}>
              {headerAndVideo}
              {inVideoWave}
            </Box>
            {controls}
          </Box>
        </Box>
        <AvatarSessionContextPanel
          objective={sessionObjective}
          status={sessionStatus}
          domain={domain}
          focusDisplayName={focusDisplayName}
          transcriptLines={liveTranscript}
          isCoachingMode={isCoachingMode}
          doctorName={doctorName}
          isAiSpeaking={isAiSpeaking}
        />
        {sessionErrorModal}
      </Box>
    );
  }

  return (
    <Box
      className={className}
      sx={containerStyles(showCountdown, lessonDialogue)}
    >
      {showStartCountdown ? (
        <Box sx={countdownOverlayStyles}>
          <CountdownStart
            onCountdownComplete={handleCountdownComplete}
            instructionText="Get ready to start the dialogue"
          />
        </Box>
      ) : (
        headerAndVideo
      )}

      {controls}
      {sessionErrorModal}
    </Box>
  );
};
